import { ChangeEvent, useEffect } from "react";
import { getEmployeeList } from "src/apis/employeeApi";
import { CodeForm } from "src/components/common/form/CodeForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { TQC2100_R01HeaderDto } from "./Dto_TQC2100_R01";
import { useTQC2100_R01Store } from "./Store_TQC2100_R01";

interface Props {
	defaultTQC2100_R01HeaderRow: TQC2100_R01HeaderDto;
}

/** 선택한 측정 헤더의 검사자, 검사구분, 승인자를 표시하고 편집하는 영역 */
export const Info_TQC2100_R01 = ({ defaultTQC2100_R01HeaderRow }: Props) => {
	const {
		editTQC2100_R01,
		setEditTQC2100_R01,
		selectedTQC2100_R01,
	} = useTQC2100_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { inspectionTypeList } = useCommonCodeStore();
	const editData = editTQC2100_R01 ?? defaultTQC2100_R01HeaderRow;

	useEffect(() => {
		// 조회 행 선택 시 서버 헤더 값을 편집용 상태에 반영한다.
		setEditTQC2100_R01({ ...defaultTQC2100_R01HeaderRow, ...selectedTQC2100_R01 });
	}, [selectedTQC2100_R01, defaultTQC2100_R01HeaderRow, setEditTQC2100_R01]);

	const fields: InfoCodeSearchFieldConfig[] = [
		// 검사자와 승인자는 공통 사원검색 모달을 동일한 방식으로 사용한다.
		{
			fieldName: "inspectorEmp",
			category: "검사자",
			focusId: "inspectorEmpInput_TQC2100_R01",
			responseMapping: { empNo: "inspectorEmp", empNm: "inspectorEmpNm" },
			searchFunction: async (code) =>
				(await getEmployeeList("all")).data.data.find((item) => item.empNo === code),
			extraFilter: () => ({
				inspectorEmp: editData.inspectorEmp,
				inspectorEmpNm: editData.inspectorEmpNm,
			}),
		},
		{
			fieldName: "approvorEmpno",
			category: "승인자",
			focusId: "approvorEmpnoInput_TQC2100_R01",
			responseMapping: { empNo: "approvorEmpno", empNm: "approvorEmpNm" },
			searchFunction: async (code) =>
				(await getEmployeeList("all")).data.data.find((item) => item.empNo === code),
			extraFilter: () => ({
				approvorEmpno: editData.approvorEmpno,
				approvorEmpNm: editData.approvorEmpNm,
			}),
		},
	];
	const { getFieldHandlers } = useInfoFormEvents({
		editData,
		setEditData: setEditTQC2100_R01,
		defaultData: defaultTQC2100_R01HeaderRow,
		infoFieldConfigs: fields,
		setCodeSearchModalProps,
		formName: "TQC2100_R01",
	});
	const {
		onChange: handleInspectorChange,
		onDoubleClick: handleInspectorDoubleClick,
		onEnterKeyDown: handleInspectorEnterKeyDown,
	} = getFieldHandlers("inspectorEmp");
	const {
		onChange: handleApprovorChange,
		onDoubleClick: handleApprovorDoubleClick,
		onEnterKeyDown: handleApprovorEnterKeyDown,
	} = getFieldHandlers("approvorEmpno");
	const handleChange = (event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		setEditTQC2100_R01({ ...editData, [event.target.name]: event.target.value });
	};
	const style = {
		wrapperStyle: "w-[420px] min-w-[420px] mr-[10px] mb-[5px]",
		titleStyle: "w-[80px] min-w-[80px]",
		codeInputStyle: "w-[120px] min-w-[120px]",
		custNmInputStyle: "w-[200px] min-w-[200px]",
	};

	return (
		<div className="flex items-center flex-wrap px-[12px] pt-[8px] pb-[4px]">
			<CodeForm
				label="검사자"
				value={editData.inspectorEmp}
				custNmValue={editData.inspectorEmpNm}
				editable={true}
				handleFoucsId="inspectorEmpInput_TQC2100_R01"
				name="inspectorEmp"
				wrapperOption={style}
				handleChange={handleInspectorChange}
				onDoubleClick={handleInspectorDoubleClick}
				onKeyDown={handleInspectorEnterKeyDown}
			/>
			<DropDownForm
				title="검사구분"
				value={editData.inspectType || "FPI"}
				name="inspectType"
				initLabelLess
				data={inspectionTypeList}
				editable={false}
				onChange={handleChange}
			/>
			<CodeForm
				label="승인자"
				value={editData.approvorEmpno}
				custNmValue={editData.approvorEmpNm}
				editable={true}
				handleFoucsId="approvorEmpnoInput_TQC2100_R01"
				name="approvorEmpno"
				wrapperOption={style}
				handleChange={handleApprovorChange}
				onDoubleClick={handleApprovorDoubleClick}
				onKeyDown={handleApprovorEnterKeyDown}
			/>
		</div>
	);
};
